import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import "./OrderTracking1.css";

const BASE_URL = "https://ecom.cyberathon.com";

const OrderTracking1 = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const user_id = sessionStorage.getItem("userId");

  const [orderData, setOrderData] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user_id) {
      setError("Please login first");
      setLoading(false);
      return;
    }
    
    fetchOrderAndTracking();
  }, [orderId, user_id]);

  const fetchOrderAndTracking = async () => {
    try {
      setLoading(true);
      setError(null);

      // ✅ STEP 1: Get order details
      const orderResponse = await axios.post(
        `${BASE_URL}/index.php/Api/get_by_id_new_order`,
        { 
          order_id: Number(orderId),
          user_id: user_id
        }
      );

      if (!orderResponse.data.status || orderResponse.data.status === "false") {
        setError("Order not found");
        setLoading(false);
        return;
      }

      const order = orderResponse.data.orders;
      setOrderData(order);

      const awbNumber = order.awb_number;

      if (!awbNumber || awbNumber === "0" || awbNumber === "") {
        console.warn("⚠️ AWB not available");
        setLoading(false);
        return;
      }

      // ✅ STEP 2: Fetch live tracking from BigShip
      try {
        const bigshipToken = sessionStorage.getItem("bigship_token");
        
        const trackingResponse = await axios.get(
          "/api-bigship/api/tracking",
          {
            params: {
              tracking_type: "awb",
              tracking_id: awbNumber
            },
            headers: bigshipToken ? {
              Authorization: `Bearer ${bigshipToken}`
            } : {}
          }
        );

        if (trackingResponse.data.success && trackingResponse.data.data) {
          setTrackingData(trackingResponse.data.data);
        }
      } catch (trackingError) {
        console.error("❌ Tracking Error:", trackingError);
      }

    } catch (err) {
      console.error("❌ Error:", err);
      setError("Failed to load tracking information");
    } finally {
      setLoading(false);
    }
  };

  // 🎯 Get step index from status
  const getStepFromStatus = (status) => {
    const s = status?.toUpperCase() || "";
    if (s.includes("NEW") || s.includes("PENDING")) return 0;
    if (s.includes("PICKUP") || s.includes("READY")) return 1;
    if (s.includes("TRANSIT") || s.includes("SHIPPED") || s.includes("DISPATCHED")) return 2;
    if (s.includes("DELIVERED")) return 3;
    if (s.includes("COMPLETED")) return 4;
    return 0;
  };

  const formatDate = (dateStr) => dateStr || "N/A";

  // 📊 Loading
  if (loading) {
    return (
      <div className="tracking-page">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
          <h4 className="mt-3">Loading tracking...</h4>
        </div>
      </div>
    );
  }

  // ❌ Error
  if (error) {
    return (
      <div className="tracking-page">
        <div className="text-center py-5">
          <div className="alert alert-danger">{error}</div>
          <button className="btn btn-primary" onClick={() => navigate(-1)}>
            ← Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="tracking-page">
        <div className="text-center py-5">
          <div className="alert alert-warning">Order not found</div>
          <button className="btn btn-primary" onClick={() => navigate(-1)}>
            ← Go Back
          </button>
        </div>
      </div>
    );
  }

  // 🎯 Progress Steps
  const steps = [
    { label: "New", icon: "1" },
    { label: "Ready to Pick", icon: "2" },
    { label: "In Transit", icon: "3" },
    { label: "Delivered", icon: "4" },
    { label: "Completed", icon: "5" }
  ];

  const currentStatus = trackingData?.order_detail?.current_tracking_status 
    || orderData.order_status;

  const currentStep = getStepFromStatus(currentStatus);

  return (
    <div className="tracking-page">
      {/* Header */}
      <div className="tracking-header">
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h2 className="page-title">Track Order</h2>
      </div>

      {/* ========== TRACKING PROGRESS ========== */}
      <div className="tracking-card">
        <h3 className="card-title">Tracking Progress</h3>

        {/* Horizontal Stepper */}
        <div className="stepper-container">
          {steps.map((step, index) => {
            const isCompleted = index < currentStep;
            const isActive = index === currentStep;

            return (
              <div key={index} className="stepper-item">
                {/* Circle */}
                <div className={`stepper-circle ${isCompleted ? "completed" : ""} ${isActive ? "active" : ""}`}>
                  {isCompleted ? "✓" : step.icon}
                </div>
                
                {/* Label */}
                <div className="stepper-label">{step.label}</div>

                {/* Line */}
                {index < steps.length - 1 && (
                  <div className={`stepper-line ${isCompleted ? "completed" : ""}`} />
                )}
              </div>
            );
          })}
        </div>

        {/* Status Pills */}
        {/* <div className="status-pills">
          <span className="pill pill-warning">Pending</span>
          <span className="pill pill-info">Bill Sent</span>
          <span className="pill pill-danger">Cancelled</span>
          <span className="pill pill-danger">RTO</span>
          <span className="pill pill-danger">Rejected</span>
        </div> */}
      </div>

      {/* ========== SCAN HISTORY ========== */}
      {trackingData?.scan_histories && trackingData.scan_histories.length > 0 ? (
        <div className="tracking-card">
          <h3 className="card-title">Scan History</h3>

          <div className="scan-timeline">
            {trackingData.scan_histories.map((scan, index) => (
              <div key={index} className="scan-item">
                {/* Marker */}
                <div className="scan-marker">
                  <div className="scan-dot"></div>
                  {index !== trackingData.scan_histories.length - 1 && (
                    <div className="scan-line"></div>
                  )}
                </div>

                {/* Content */}
                <div className="scan-content">
                  <div className="scan-header">
                    <h5 className="scan-status">{scan.scan_status}</h5>
                    <span className="scan-date">{formatDate(scan.scan_datetime)}</span>
                  </div>

                  {scan.scan_location && (
                    <p className="scan-location">
                      📍 <strong>Location:</strong> {scan.scan_location}
                    </p>
                  )}

                  {scan.scan_remarks && scan.scan_remarks.trim() !== "" && (
                    <p className="scan-remarks">
                      💬 <strong>Remarks:</strong> {scan.scan_remarks}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        orderData.awb_number && (
          <div className="tracking-card">
            <div className="alert alert-info">
              ℹ️ No scan history available yet. Please check back later.
            </div>
          </div>
        )
      )}

      {/* AWB Not Assigned */}
      {(!orderData.awb_number || orderData.awb_number === "0") && (
        <div className="tracking-card">
          <div className="alert alert-warning">
            ⏳ Shipment not dispatched yet. Tracking will be available once shipped.
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderTracking1;